import React, { useEffect, useState } from "react";
import { api, fmtError, eur } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { reference: "", produit: "", famille: "", fournisseur: "", territoire: "Guadeloupe",
  prix_ht: 0, tva: 8.5, unite: "u", conditionnement: "", date_tarif: "", source: "", lien_source: "", commentaire: "" };

export default function Suppliers() {
  const [prices, setPrices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/price-book", { params: { q: search || undefined } }).then((r) => setPrices(r.data)).catch(() => {});
    api.get("/contacts", { params: { categorie: "fournisseur" } }).then((r) => setSuppliers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/price-book", { ...form, prix_ht: parseFloat(form.prix_ht) || 0, tva: parseFloat(form.tva) || 0 });
      toast.success("Tarif ajouté");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="space-y-5" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Fournisseurs & Prix</h1>
        <Button data-testid="new-price-button" onClick={() => setOpen(true)} className="bg-amber-500 text-slate-900 font-bold hover:bg-amber-400 h-12">
          <Plus className="h-5 w-5 mr-1" /> Tarif
        </Button>
      </div>

      <section>
        <h2 className="font-display text-lg font-bold uppercase mb-2">Fournisseurs référencés</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="suppliers-list">
          {suppliers.map((s) => (
            <div key={s.id} className="bg-card rounded-xl border border-border p-4" data-testid={`supplier-${s.id}`}>
              <div className="font-bold text-sm">{s.nom_commercial || s.raison_sociale}</div>
              <div className="text-xs text-muted-foreground">{s.raison_sociale}</div>
              {s.siren && <div className="text-xs font-mono-num mt-1">SIREN {s.siren}</div>}
              <div className="text-xs text-muted-foreground mt-1">{s.adresse}</div>
              {s.secteur && <div className="text-xs mt-1">{s.secteur}</div>}
              <div className="flex gap-2 mt-2">
                {s.telephone && <a href={`tel:${s.telephone}`} data-testid={`supplier-call-${s.id}`} className="text-xs font-bold text-amber-600">{s.telephone}</a>}
                {s.site_web && <a href={s.site_web} target="_blank" rel="noreferrer" data-testid={`supplier-web-${s.id}`} className="text-xs font-bold text-sky-600 inline-flex items-center gap-1">Catalogue <ExternalLink className="h-3 w-3" /></a>}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">Source : {s.source_contact}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="font-display text-lg font-bold uppercase">Price book</h2>
          <div className="flex gap-2 flex-1 max-w-md ml-auto">
            <Input data-testid="price-search" placeholder="Rechercher un produit…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
            <Button data-testid="price-search-button" onClick={load} variant="outline"><Search className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border overflow-x-auto" data-testid="price-book-table">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-teca-900 text-white text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2.5">Réf</th><th className="text-left px-3 py-2.5">Produit</th>
                <th className="text-left px-3 py-2.5">Famille</th><th className="text-right px-3 py-2.5">Prix HT</th>
                <th className="text-left px-3 py-2.5">Unité</th><th className="text-left px-3 py-2.5">Date tarif</th><th className="text-left px-3 py-2.5">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {prices.map((p) => (
                <tr key={p.id} data-testid={`price-row-${p.reference}`} className="hover:bg-muted/60">
                  <td className="px-3 py-2.5 font-mono-num text-xs">{p.reference}</td>
                  <td className="px-3 py-2.5 font-semibold">{p.produit}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{p.famille}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num font-bold">{eur(p.prix_vente_ht || p.prix_ht)}</td>
                  <td className="px-3 py-2.5">{p.unite}</td>
                  <td className="px-3 py-2.5 text-xs">{p.date_tarif}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{p.source}</td>
                </tr>
              ))}
              {prices.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground" data-testid="prices-empty">Aucun tarif.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="price-form-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Nouveau tarif</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="price-produit" required placeholder="Produit / prestation *" value={form.produit} onChange={(e) => setForm({ ...form, produit: e.target.value })} className="py-3.5" />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="price-reference" placeholder="Référence" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              <Input data-testid="price-famille" placeholder="Famille" value={form.famille} onChange={(e) => setForm({ ...form, famille: e.target.value })} />
            </div>
            <Input data-testid="price-fournisseur" placeholder="Fournisseur" value={form.fournisseur} onChange={(e) => setForm({ ...form, fournisseur: e.target.value })} />
            <div className="grid grid-cols-3 gap-2">
              <Input data-testid="price-ht" type="number" step="any" placeholder="Prix HT *" required value={form.prix_ht} onChange={(e) => setForm({ ...form, prix_ht: e.target.value })} />
              <Input data-testid="price-tva" type="number" step="any" placeholder="TVA %" value={form.tva} onChange={(e) => setForm({ ...form, tva: e.target.value })} />
              <Input data-testid="price-unite" placeholder="Unité" value={form.unite} onChange={(e) => setForm({ ...form, unite: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="price-date" type="date" value={form.date_tarif} onChange={(e) => setForm({ ...form, date_tarif: e.target.value })} />
              <Input data-testid="price-source" placeholder="Source (catalogue, lien…)" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
            </div>
            <Button data-testid="price-submit" type="submit" className="w-full h-14 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">ENREGISTRER LE TARIF</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
